/**
 * Guest Feedback admin screen.
 *
 * Lists submissions from `GET /admin/guest-feedbacks` (new endpoint added in
 * `backend/src/controllers/guestFeedback.js`). Mirrors the visual pattern
 * of `ServiceRequestsScreen` so ops doesn't have to re-learn the layout.
 *
 * The legacy `/admin/get-order-feedbacks` endpoint and the old
 * `OrderFeedbackTable` are intentionally left untouched (no callers remain
 * in the admin app — verified during pre-read) so we don't break any
 * external bookmarks or scripts.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiRefreshCw, FiSearch, FiMessageCircle } from 'react-icons/fi';
import request from '../utils/request';
import GuestFeedbackDrawer from '../components/GuestFeedbackDrawer';

const STAR_FILL = '#F4B400';
const STAR_EMPTY = '#E5E7EB';

const StarRow = ({ value, size = 14 }) => {
  const safe = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${safe} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={n <= safe ? STAR_FILL : 'none'}
          stroke={n <= safe ? STAR_FILL : STAR_EMPTY}
          strokeWidth="1.5"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      ))}
    </div>
  );
};

const ARRIVAL_EXPERIENCE_LABELS = {
  loved_it: 'Loved it',
  helpful: 'Helpful',
  okay: 'Okay',
  could_be_better: 'Could be better',
};

const ARRIVAL_CONDITION_LABELS = {
  great: 'Great',
  small_issues: 'Small issues',
  missing_or_incorrect: 'Missing/incorrect',
  not_checked_yet: 'Not checked yet',
};

const BIGGEST_DIFFERENCE_LABELS = {
  saved_grocery_trip: 'Saved trip',
  easier_arrival: 'Easier arrival',
  more_prepared: 'More prepared',
  more_thoughtful: 'More thoughtful',
  more_premium: 'More premium',
  other: 'Other',
};

const arrivalExperienceTone = (val) => {
  if (val === 'loved_it' || val === 'helpful') return 'bg-emerald-50 text-emerald-700';
  if (val === 'could_be_better') return 'bg-amber-50 text-amber-700';
  return 'bg-zinc-100 text-zinc-700';
};

const arrivalConditionTone = (val) => {
  if (val === 'great') return 'bg-emerald-50 text-emerald-700';
  if (val === 'small_issues') return 'bg-amber-50 text-amber-700';
  if (val === 'missing_or_incorrect') return 'bg-red-50 text-red-700';
  return 'bg-zinc-100 text-zinc-700';
};

const Pill = ({ children, className }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${className}`}>
    {children}
  </span>
);

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const truncate = (s, n) => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

const STAR_FILTERS = [
  { id: '', label: 'All ratings' },
  { id: '5', label: '5 only' },
  { id: '4', label: '4+' },
  { id: '3', label: '3+' },
  { id: '2', label: '2+' },
  { id: '1', label: '1+' },
];

const OrderFeedback = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [minStars, setMinStars] = useState('');
  const [hasComment, setHasComment] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (minStars) params.set('minStars', minStars);
      if (hasComment) params.set('hasComment', 'true');
      if (search) params.set('search', search);
      const { data } = await request.get(
        `/admin/guest-feedbacks?${params.toString()}`
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to load guest feedback'
      );
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [minStars, hasComment, search]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  // Debounce search input → applied search. Avoids hammering the API on
  // every keystroke without forcing the user to hit Enter.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const stats = useMemo(() => {
    if (!items.length) return { avg: 0, withComment: 0 };
    const sum = items.reduce((acc, x) => acc + (x.overallStars || 0), 0);
    const withComment = items.filter((x) => x.openComment && x.openComment.trim()).length;
    return { avg: (sum / items.length).toFixed(1), withComment };
  }, [items]);

  return (
    <div className="flex-1 overflow-auto bg-zinc-50 p-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Guest Feedback</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {total} total submission{total === 1 ? '' : 's'}
              {items.length ? (
                <>
                  {' · '}avg <span className="font-semibold text-zinc-700">{stats.avg}</span>
                  {' · '}{stats.withComment} with comment
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchFeedback}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="mb-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-1">
              {STAR_FILTERS.map((opt) => (
                <button
                  key={opt.id || 'all'}
                  type="button"
                  onClick={() => setMinStars(opt.id)}
                  className={`px-3 py-1.5 text-[12px] font-medium rounded-full transition-colors ${
                    minStars === opt.id
                      ? 'bg-zinc-900 text-white'
                      : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <label className="inline-flex items-center gap-2 text-[12px] font-medium text-zinc-700">
              <input
                type="checkbox"
                checked={hasComment}
                onChange={(e) => setHasComment(e.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
              />
              With comment only
            </label>

            <div className="relative ml-auto w-full sm:w-72">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by order ID"
                className="w-full rounded-lg border border-zinc-200 bg-white pl-9 pr-3 py-2 text-[13px] text-zinc-800 placeholder:text-zinc-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>
        </div>

        {error ? (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
        ) : null}

        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Order</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Stars</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Arrival</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Condition</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Biggest win</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Comment</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {loading && !items.length ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-zinc-400">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" />
                        Loading…
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-zinc-400">
                      No feedback yet
                    </td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelected(row)}
                      className="hover:bg-zinc-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium text-zinc-900">
                        <div className="flex flex-col">
                          <span className="text-[13px]">{row.orderUniqueId}</span>
                          {row.order?.propertyTitle ? (
                            <span className="text-[11px] text-zinc-400 mt-0.5">
                              {truncate(row.order.propertyTitle, 36)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <StarRow value={row.overallStars} />
                          <span className="text-[12px] text-zinc-500">
                            {row.overallStars}/5
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {row.arrivalExperience ? (
                          <Pill className={arrivalExperienceTone(row.arrivalExperience)}>
                            {ARRIVAL_EXPERIENCE_LABELS[row.arrivalExperience] ||
                              row.arrivalExperience}
                          </Pill>
                        ) : (
                          <span className="text-[11px] text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.arrivalCondition ? (
                          <Pill className={arrivalConditionTone(row.arrivalCondition)}>
                            {ARRIVAL_CONDITION_LABELS[row.arrivalCondition] ||
                              row.arrivalCondition}
                          </Pill>
                        ) : (
                          <span className="text-[11px] text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.biggestDifference ? (
                          <Pill className="bg-zinc-100 text-zinc-700">
                            {BIGGEST_DIFFERENCE_LABELS[row.biggestDifference] ||
                              row.biggestDifference}
                          </Pill>
                        ) : (
                          <span className="text-[11px] text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-600 max-w-[240px]">
                        {row.openComment ? (
                          <span className="inline-flex items-start gap-1.5">
                            <FiMessageCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400" />
                            <span className="text-[12px] line-clamp-2">
                              {truncate(row.openComment, 80)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-[11px] text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-500 whitespace-nowrap text-[12px]">
                        {formatDate(row.submittedAt || row.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected ? (
        <GuestFeedbackDrawer feedback={selected} onClose={() => setSelected(null)} />
      ) : null}
    </div>
  );
};

export default OrderFeedback;
